import { useTranslation } from "@sagot/i18n";
import { Icon, Progress, Skeleton, StatCard } from "@sagot/ui";
import { formatBytes, formatNumber, formatRelative } from "../shared/format";
import { useBillingOverview } from "../shared/use-shared-queries";
import type { KnowledgeSummary } from "./source-model";

const K = "deployKnowledge.knowledge.stats";
const PERCENT = 100;
const STAT_SLOTS = 5;

function StorageCard() {
  const { t } = useTranslation();
  const billing = useBillingOverview();
  const meter = billing.data?.storage_mb;
  const limit = meter?.limit ?? null;
  const used = meter?.used ?? 0;
  const pct = limit ? Math.min(PERCENT, Math.round((used / limit) * PERCENT)) : null;
  return (
    <StatCard
      label={t(`${K}.storage`)}
      icon="database"
      value={pct === null ? (meter ? `${formatNumber(used, "en")} MB` : "—") : `${String(pct)}%`}
      footnote={
        <>
          {limit ? <Progress className="mb-1.5 mt-1" value={used} max={limit} label={t(`${K}.storageLabel`)} /> : null}
          <span className="font-semibold">
            {limit ? t(`${K}.storageOf`, { used, limit }) : meter ? t(`${K}.storageUsed`, { used }) : t(`${K}.storageUnknown`)}
          </span>
        </>
      }
    />
  );
}

export function KnowledgeStatsSkeleton() {
  return (
    <section className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5" aria-hidden="true">
      {Array.from({ length: STAT_SLOTS }, (_, i) => (
        <div key={i} className="card card-body space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-7 w-16" />
          <Skeleton className="h-3 w-32" />
        </div>
      ))}
    </section>
  );
}

/** Five summary cards: sources by kind, chunks, uploaded bytes, plan storage, last sync + attention. */
export function KnowledgeStats({ summary }: { summary: KnowledgeSummary }) {
  const { t, i18n } = useTranslation();
  const { counts } = summary;
  const attention = summary.attention.length + summary.warnings.length;
  return (
    <section className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
      <StatCard
        label={t(`${K}.sources`)}
        icon="layers"
        value={formatNumber(summary.total, i18n.language)}
        footnote={<span className="font-semibold">{t(`${K}.sourcesBreakdown`, { files: counts.files, sites: counts.websites, other: counts.qa + counts.text })}</span>}
      />
      <StatCard
        label={t(`${K}.chunks`)}
        icon="hash"
        value={formatNumber(summary.chunks, i18n.language)}
        footnote={<span className="font-semibold">{summary.excluded > 0 ? t(`${K}.chunksExcluded`, { count: summary.excluded }) : t(`${K}.chunksNoneExcluded`)}</span>}
      />
      <StatCard label={t(`${K}.uploaded`)} icon="file-text" value={formatBytes(summary.uploadedBytes)} footnote={<span className="font-semibold">{t(`${K}.uploadedNote`)}</span>} />
      <StorageCard />
      <StatCard
        className="col-span-2 md:col-span-1"
        label={t(`${K}.lastSync`)}
        icon="clock"
        value={formatRelative(summary.lastSyncAt, i18n.language)}
        footnote={
          attention > 0 ? (
            <span className="flex items-center gap-1 font-semibold text-warning">
              <Icon name="alert-triangle" className="size-3.5" />
              {t(`${K}.attention`, { count: attention })}
            </span>
          ) : summary.syncing > 0 ? (
            <span className="flex items-center gap-1 font-semibold text-fg-muted">
              <Icon name="refresh-cw" className="size-3.5" />
              {t(`${K}.syncing`, { count: summary.syncing })}
            </span>
          ) : (
            <span className="flex items-center gap-1 font-semibold text-success">
              <Icon name="check-circle" className="size-3.5" />
              {t(`${K}.allHealthy`)}
            </span>
          )
        }
      />
    </section>
  );
}
